/**
 * Who is allowed to draw the open object's name.
 *
 * `RunNavShell` already draws a header for whatever the reader opened — icon,
 * title, `Type · OBJ_NAME`, close. Two panes inside it drew that name a second
 * time (the transaction list and the document repository), because they are
 * ALSO mounted where nothing else names the object: the designer's Live
 * Preview, the Document Designer's run tab, a Smart Page embed. Both headings
 * are right on their own; together they read as a bug.
 *
 * So the shell publishes WHICH object its chrome names, and a self-titled pane
 * asks before drawing its own heading. The question is deliberately about
 * IDENTITY rather than "am I inside a shell": a transaction list embedded in a
 * Smart Page sits under the same provider, but the shell up there names the
 * PAGE — that list is a different object and keeps its title.
 *
 * A pane with no provider above it (every designer preview) keeps its heading
 * unchanged, which is what makes this safe to adopt one pane at a time.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react'

export interface ObjectChrome {
  /** Navigation object type of the object the chrome names ('T', 'K', …). */
  objType: string
  /** Its object name — the identity, not the display title. */
  objName: string
  /**
   * The shell's full screen for this object, when the chrome offers one. A
   * pane's own expand button drives THIS (hiding the whole shell) instead of
   * overlaying itself; absent → the pane keeps its own full-screen overlay.
   */
  fullScreen?: { on: boolean; set: (on: boolean) => void }
}

const ObjectChromeContext = createContext<ObjectChrome | null>(null)

/** Wraps the pane a host chrome has just drawn a header for. */
export function ObjectChromeProvider({ objType, objName, fullScreen, children }: {
  objType: string
  objName: string
  fullScreen?: ObjectChrome['fullScreen']
  children: ReactNode
}) {
  const fsOn = fullScreen?.on
  const fsSet = fullScreen?.set
  const value = useMemo<ObjectChrome>(() => ({
    objType, objName,
    ...(fsSet ? { fullScreen: { on: !!fsOn, set: fsSet } } : {}),
  }), [objType, objName, fsOn, fsSet])
  return (
    <ObjectChromeContext.Provider value={value}>
      {children}
    </ObjectChromeContext.Provider>
  )
}

/** The object the surrounding chrome names, or null when nothing does. */
export function useObjectChrome(): ObjectChrome | null {
  return useContext(ObjectChromeContext)
}

/**
 * True when the surrounding chrome's header already shows THIS object's name,
 * i.e. a heading drawn here would be the same name twice. False with no chrome
 * at all, and false for a different object (an embedded list, a child view).
 *
 * `objType` is optional and only tightens the match; pass it where the caller
 * knows it, so an obj_name that repeats across types cannot collide.
 */
export function useHostNamesObject(objName: string, objType?: string): boolean {
  const chrome = useObjectChrome()
  if (!chrome || !objName) return false
  return chrome.objName === objName && (!objType || chrome.objType === objType)
}
